import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../common/Button';
import SearchableSelect, { SelectOption } from '../../common/SearchableSelect';
import Tabs from '../../common/Tabs';
import AITaskModelsService from '../../../services/ai-task-models.service';
import {
  AITaskModelsPayload,
  TAIProviderCatalogEntry,
  TAITaskModel,
} from '../../../types/ai-task-models.types';
import { ToastType } from '../../../types/common.types';
import { useAppContext } from '../../../utils/context/app-context';

const SOURCE_BADGES: Record<
  TAITaskModel['source'],
  { label: string; className: string }
> = {
  config: { label: 'Saved', className: 'bg-[#e8f3ec] text-[#256b3d]' },
  env: { label: 'Env override', className: 'bg-[#fdf2e3] text-[#8a5a11]' },
  default: { label: 'Default', className: 'bg-[rgba(0,0,0,.06)] text-[rgba(0,0,0,.6)]' },
};

const buildProviderOptions = (
  providers: TAIProviderCatalogEntry[]
): SelectOption[] =>
  providers.map(({ value, label }) => ({ value, label }));

/**
 * The model list is filtered by the selected provider and by what the task
 * needs: an image-generation task should not be offered a chat model.
 *
 * The raw model id rides along as the description so the dropdown shows both
 * the marketing name and the string the code sends to the provider.
 */
const buildModelOptions = (
  providers: TAIProviderCatalogEntry[],
  provider: string,
  kind: TAITaskModel['kind']
): SelectOption[] => {
  const entry = providers.find((item) => item.value === provider);
  if (!entry) return [];
  return entry.models
    .filter((model) => model.kind === kind)
    .map(({ value, label }) => ({ value, label, description: value }));
};

const AiTaskModels = () => {
  const { setToastInfo } = useAppContext();
  const [payload, setPayload] = useState<AITaskModelsPayload | null>(null);
  const [tasks, setTasks] = useState<TAITaskModel[]>([]);
  const [activeGroup, setActiveGroup] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const applyPayload = useCallback((data: AITaskModelsPayload) => {
    setPayload(data);
    setTasks(data.tasks);
    setActiveGroup((current) =>
      current && data.tasks.some((task) => task.group === current)
        ? current
        : data.tasks[0]?.group || ''
    );
  }, []);

  const showError = useCallback(
    (message: string) =>
      setToastInfo({ isOpen: true, type: ToastType.ERROR, message }),
    [setToastInfo]
  );

  const loadTaskModels = useCallback(async () => {
    try {
      const response = await AITaskModelsService.getAITaskModels();
      if (response.data.data) {
        applyPayload(response.data.data);
      } else {
        showError(response.data.message);
      }
    } catch (err) {
      showError('Something went wrong, please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [applyPayload, showError]);

  useEffect(() => {
    loadTaskModels();
  }, [loadTaskModels]);

  /**
   * Only the tabs that actually have tasks, in registry order — a group with no
   * tasks is a tab that leads nowhere.
   */
  const groupTabs = useMemo(() => {
    if (!payload) return [];
    const present = new Set(tasks.map((task) => task.group));
    return payload.groups.filter((group) => present.has(group.value));
  }, [payload, tasks]);

  const visibleTasks = useMemo(
    () => tasks.filter((task) => task.group === activeGroup),
    [tasks, activeGroup]
  );

  /**
   * Only the tasks the admin actually touched.
   *
   * Saving every rendered task would write a row for each one, which silently
   * freezes today's shipped defaults into the database and converts any env
   * override into a saved value — after which a code-side default change would
   * never reach the platform again.
   */
  const changedTasks = useMemo(() => {
    if (!payload) return [];
    return tasks.filter((task) => {
      const original = payload.tasks.find(
        (item) => item.taskKey === task.taskKey
      );
      return (
        !!original &&
        (original.provider !== task.provider || original.model !== task.model)
      );
    });
  }, [payload, tasks]);

  const updateTask = (taskKey: string, changes: Partial<TAITaskModel>) =>
    setTasks((current) =>
      current.map((task) =>
        task.taskKey === taskKey ? { ...task, ...changes } : task
      )
    );

  const handleProviderChange = (task: TAITaskModel, provider: string) => {
    if (!provider || !payload) return;
    // The current model almost certainly belongs to the old provider, so move to
    // that provider's first option rather than leaving an unreachable model id.
    const options = buildModelOptions(payload.providers, provider, task.kind);
    updateTask(task.taskKey, {
      provider,
      model: options[0]?.value || '',
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const missing = changedTasks.find((task) => !task.model.trim());
      if (missing) {
        showError(`${missing.label} needs a model.`);
        return;
      }

      const response = await AITaskModelsService.updateAITaskModels(
        changedTasks.map((task) => ({
          taskKey: task.taskKey,
          provider: task.provider,
          modelName: task.model,
        }))
      );

      if (response.data.data) {
        applyPayload(response.data.data);
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
      } else {
        showError(response.data.message);
      }
    } catch (err) {
      showError('Something went wrong, please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async (taskKey: string) => {
    try {
      const response = await AITaskModelsService.resetAITaskModel(taskKey);
      if (response.data.data) {
        applyPayload(response.data.data);
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
      } else {
        showError(response.data.message);
      }
    } catch (err) {
      showError('Something went wrong, please try again later.');
    }
  };

  if (isLoading) {
    return (
      <p className="py-10 text-center text-[rgba(0,0,0,.5)]">
        Loading AI models...
      </p>
    );
  }

  if (!payload) {
    return (
      <p className="py-10 text-center text-[rgba(0,0,0,.5)]">
        AI models could not be loaded.
      </p>
    );
  }

  const providerOptions = buildProviderOptions(payload.providers);

  return (
    <div>
      <p className="mb-5 text-[14px] text-[rgba(0,0,0,.6)]">
        The model each part of the platform runs on. A task with no saved
        selection uses the default shipped with the code, so nothing here has to
        be filled in for the platform to work.
      </p>

      <Tabs
        items={groupTabs}
        active={activeGroup}
        onChange={setActiveGroup}
        className="mb-6"
      />

      <div className="flex flex-col gap-5">
        {visibleTasks.map((task) => {
          const badge = SOURCE_BADGES[task.source];
          const modelOptions = buildModelOptions(
            payload.providers,
            task.provider,
            task.kind
          );

          return (
            <div
              key={task.taskKey}
              className="border border-[rgba(0,0,0,.12)] rounded-2xl p-5"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-[16px] font-semibold">{task.label}</h4>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  </div>
                  <p className="mt-1 text-[13px] text-[rgba(0,0,0,.55)]">
                    {task.description}
                  </p>
                </div>
                {task.source === 'config' && (
                  <button
                    type="button"
                    onClick={() => handleReset(task.taskKey)}
                    className="shrink-0 text-[13px] underline text-[rgba(0,0,0,.55)] hover:text-[#333]"
                  >
                    Reset to default
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SearchableSelect
                  label="Provider"
                  value={task.provider}
                  options={providerOptions}
                  onChange={(provider) => handleProviderChange(task, provider)}
                  placeholder="Search providers..."
                  disabled={!!task.providerLocked}
                  hint={
                    task.providerLocked
                      ? 'Fixed — this task calls the provider SDK directly.'
                      : undefined
                  }
                />
                <SearchableSelect
                  label="Model"
                  value={task.model}
                  options={modelOptions}
                  onChange={(model) => updateTask(task.taskKey, { model })}
                  placeholder="Search models..."
                  creatable
                  hint={`Default: ${task.defaultModel}${
                    task.envVar ? ` · env: ${task.envVar}` : ''
                  }`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-8 pb-6">
        {!!changedTasks.length && (
          <p className="mb-3 text-[13px] text-[rgba(0,0,0,.55)]">
            {changedTasks.length} unsaved change
            {changedTasks.length === 1 ? '' : 's'} across all tabs.
          </p>
        )}
        <Button
          type="button"
          disabled={!changedTasks.length || isSaving}
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : 'Update AI Models'}
        </Button>
      </div>
    </div>
  );
};

export default AiTaskModels;
